import { useEffect, useRef, useState } from 'react';
import { black, gray } from 'utils/style/colors';
import styled from 'styled-components';
import { IranMap } from 'components/IranMap';
import { useTranslation } from 'react-i18next';
import { convertDigitToProperFormat } from 'utils/number';

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  color: ${black};
`;

const SquereBadge = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  color: ${black};
  background-color: ${(props) => props.color};
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  padding-inline-start: 0.5rem;

  @media screen and (max-width: 600px) {
    & * {
      margin: 0;
    }
  }
`;

interface IMapProps {
  mapData: { [key: string]: number };
  tooltipTitle: string;
  color?: string;
  onClickOverProvince?: (province: string) => void | undefined;
}

function hexToRgba(hex: string | undefined | null) {
  // Remove leading #
  if (!hex) return null;
  hex = hex.replace(/^#/, '');

  // If shorthand (#fff), expand
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  // const a = hex.length === 8 ? parseInt(hex.substring(6, 8), 16) / 255 : 1;

  return `${r}, ${g}, ${b}`;
}

const nameMap: Record<string, string> = {
  اردبیل: 'ardabil',
  اصفهان: 'isfahan',
  البرز: 'alborz',
  ایلام: 'ilam',
  'آذربایجان شرقی': 'eastAzerbaijan',
  'آذربایجان غربی': 'westAzerbaijan',
  بوشهر: 'bushehr',
  تهران: 'tehran',
  'چهارمحال و بختیاری': 'chaharmahalandBakhtiari',
  // 'چهارمحال بختیاری': 'chaharmahalandBakhtiari',
  'خراسان جنوبی': 'southKhorasan',
  'خراسان رضوی': 'razaviKhorasan',
  'خراسان شمالی': 'northKhorasan',
  خوزستان: 'khuzestan',
  زنجان: 'zanjan',
  سمنان: 'semnan',
  'سیستان و بلوچستان': 'sistanAndBaluchestan',
  فارس: 'fars',
  قزوین: 'qazvin',
  قم: 'qom',
  کردستان: 'kurdistan',
  کرمانشاه: 'kermanshah',
  کرمان: 'kerman',
  'کهگیلویه و بویراحمد': 'kohgiluyehAndBoyerAhmad',
  // 'کهگیلویه بویراحمد': 'kohgiluyehAndBoyerAhmad',
  // 'کهکیلویه بویراحمد': 'kohgiluyehAndBoyerAhmad',
  'کهکیلویه و بویراحمد': 'kohgiluyehAndBoyerAhmad',
  گلستان: 'golestan',
  گیلان: 'gilan',
  لرستان: 'lorestan',
  مازندران: 'mazandaran',
  مرکزی: 'markazi',
  هرمزگان: 'hormozgan',
  همدان: 'hamadan',
  یزد: 'yazd',
};
const IranMapWithBadge: React.FC<IMapProps> = ({
  mapData,
  tooltipTitle,
  color,
  onClickOverProvince,
}) => {
  const { t } = useTranslation('general');

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current?.getBoundingClientRect();
        setDimensions({ width, height });
      }
      setWindowWidth(window.innerWidth);
    };

    updateSize(); // Get size on mount

    window.addEventListener('resize', updateSize); // Update on window resize
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const data = Object.fromEntries(
    Object.entries(mapData).map((it) => [
      nameMap[
        Object.keys(nameMap).find((f) => {
          return it[0].includes(f);
        }) || 0
      ],
      it[1] || 0,
    ])
  );
  const getProvinceValue = (value: unknown) => {
    if (typeof value === 'number') return value;
    if (typeof value === 'object' && value !== null) {
      const firstValue = Object.values(value as Record<string, unknown>).at(0);
      if (typeof firstValue === 'number') return firstValue;
    }
    return 0;
  };

  const defineColorRange = (count: number) => {
    const values = Object.values(mapData).map((provinceData) =>
      Object.values(provinceData).at(0)
    );
    const min = Math.min(...values);
    const max = Math.max(...values);

    const alpha = Math.max(0.1, Math.min(1, (count - min) / (max - min)));

    if (count === 0) return gray;

    return typeof color === 'string' && color.startsWith('#')
      ? `rgba(${hexToRgba(color)}, ${alpha})`
      : `rgba(${color ?? defaultColor}, ${alpha})`;
  };

  const provinceEntries = Object.keys(nameMap).map((key) => {
    const enKey = nameMap[key];
    const provinceValue = getProvinceValue(data?.[enKey]);
    return { name: key, value: provinceValue };
  });
  const topThreeMax = [...provinceEntries]
    .sort((a, b) => b.value - a.value)
    .slice(0, 3);
  const bottomThreeMin = [...provinceEntries]
    .sort((a, b) => a.value - b.value)
    .slice(0, 3);

  const defaultColor = '186, 23, 39';
  return (
    <MapContainer ref={containerRef}>
      {windowWidth > 600 &&
        topThreeMax.length > 0 &&
        bottomThreeMin.length > 0 && (
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              display: 'grid',
              gap: '8px',
            }}
          >
            <div className="flex flex-col">
              <h5 className="font-bold mb-2">{t('top3')}</h5>
              {topThreeMax.map((item) => (
                <BadgeRow key={`max-${item.name}`}>
                  <SquereBadge color={defineColorRange(item.value)} />
                  <span style={{ fontFamily: 'inherit' }}>
                    {t('maxMap', {
                      name: item.name,
                      value: convertDigitToProperFormat(item.value),
                    })}
                  </span>
                </BadgeRow>
              ))}
            </div>

            <div className="flex flex-col">
              <h5 className="font-bold mb-2">{t('last3')}</h5>
              {bottomThreeMin.map((item) => (
                <BadgeRow key={`min-${item.name}`}>
                  <SquereBadge color={defineColorRange(item.value)} />
                  <span style={{ fontFamily: 'inherit' }}>
                    {t('minMap', {
                      name: item.name,
                      value: convertDigitToProperFormat(item.value),
                    })}
                  </span>
                </BadgeRow>
              ))}
            </div>
          </div>
        )}
      {/* <IranMap
        data={data}
        colorRange={hexToRgba(color) || defaultColor}
        width={Math.min(dimensions.width || 300, dimensions.height || 300)}
        textColor={black}
        defaultSelectedProvince={undefined}
        deactiveProvinceColor={gray}
        // selectedProvinceColor="#F19626"
        tooltipTitle={tooltipTitle + '&nbsp:&nbsp'}
      /> */}
      <IranMap
        data={data}
        colorRange={hexToRgba(color) || defaultColor}
        width={Math.min(dimensions.width || 600, dimensions.height || 600)}
        textColor={black}
        defaultSelectedProvince={undefined}
        deactiveProvinceColor={gray}
        // selectedProvinceColor="#F19626"
        tooltipTitle={tooltipTitle}
        onClickOverProvince={onClickOverProvince}
      />
    </MapContainer>
  );
};

export default IranMapWithBadge;
